import React, { useState } from "react";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import { calculateLocationKeyHash, calculateLookupKeyHash } from "../../Utils/UtilityMethods";
import { CostCodeLookup } from "../../Utils/TypeExtensions";
import { isLastRevision, MruDaysBack, ShowSuggestions } from "../../sharedCode/HelperMethods";
import { CONTROL_MARGIN } from "../../MuiThemes";
import { getHelperText, propertyHasErrors } from "./inputHelpers";
import { getByKeyHash } from "../../Utils/ContextLookups";
import { LocationCustomControlProps } from "../../Utils/Interfaces";
import { FIELD_LIMITS } from "../../sharedCode/fieldLimits";
import dayjs from "dayjs";

export default function LocationEditor(props: LocationCustomControlProps): JSX.Element {
    const [localValue, setLocalValue] = useState<string>(limitLocation(props.initialValue ?? ""));

    const costCodeLookups = getCostCodeLookups();
    const locationOptions = costCodeLookups.map((l) => l.location).sort();

    return (
        <>
            <Autocomplete
                disabled={props.isDisabled}
                freeSolo
                options={ShowSuggestions() ? locationOptions : []}
                value={localValue ?? ""}
                inputValue={localValue ?? ""}
                onInputChange={(e, value) => {
                    handleOnInputChange(value);
                }}
                onChange={(e, v) => {
                    handleOnChange(v ?? "");
                }}
                onBlur={handleOnBlur}
                renderInput={(params) => (
                    <TextField
                        error={propertyHasErrors(props.errors, props.containerUrl, props.sourceId, props.sourceProperty)}
                        helperText={getHelperText(props.errors, props.containerUrl, props.sourceId, props.sourceProperty)}
                        {...params}
                        required={true}
                        margin={CONTROL_MARGIN}
                        label={props.labelText}
                        inputProps={{
                            ...params.inputProps,
                            maxLength: FIELD_LIMITS.location,
                        }}
                    />
                )}
            />
        </>
    );

    function limitLocation(value: string): string {
        return value.slice(0, FIELD_LIMITS.location);
    }

    // fires (1st) when the user types OR selects from dd
    function handleOnInputChange(value: string | null): void {
        setLocalValue(limitLocation(value ?? ""));
    }

    // fires when the user selects from the dd
    function handleOnChange(newValue: string): void {
        pushNewValue(newValue);
    }

    // fires when the user leaves the control
    function handleOnBlur() {
        pushNewValue(localValue ?? "");
    }

    function pushNewValue(newValue: string) {
        const limitedLocation = limitLocation(newValue).toLocaleUpperCase();
        if (limitedLocation !== (props.initialValue ?? "")) {
            const valueChanges = { [props.sourceProperty]: limitedLocation };

            const hash = calculateLocationKeyHash(props.company_id, limitedLocation);
            const foundLookup = getByKeyHash(costCodeLookups, hash ?? "");

            const changesToPush = foundLookup ? { ...valueChanges, cost_code: foundLookup.cost_code?.slice(0, FIELD_LIMITS.cost_code) } : valueChanges;

            props.mergeChanges(props.sourceId, changesToPush);
        }
    }

    function getCostCodeLookups() {
        const daysBack = MruDaysBack();

        const sourceTickets = props.ticketList.filter((t) => {
            return dayjs().diff(dayjs(t.date), "day") <= daysBack && t.company_id === props.company_id;
        });

        const costcodeMap = new Map();
        for (let i = 0; i < sourceTickets.length; i++) {
            const ticket = sourceTickets[i];
            if (isLastRevision(sourceTickets, ticket)) {
                ticket.Locations?.forEach((l) => {
                    const keyHash = calculateLookupKeyHash(ticket.company_id, l);
                    if (keyHash) {
                        const existingLookup = costcodeMap.get(keyHash);
                        if (!existingLookup) {
                            costcodeMap.set(keyHash, {
                                keyHash,
                                company_id: ticket.company_id,
                                location: l?.location,
                                cost_code: l?.cost_code,
                            } as CostCodeLookup);
                        }
                    }
                });
            }
        }
        return Array.from(costcodeMap.values()) as CostCodeLookup[];
    }
}
